import Vue from 'vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'

// Retorna os cadastros de uma equipe
/**
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 */
export async function getCadastrosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/exodontias/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

export async function getCadastrosPorMicroArea(cod_ibge, nu_ine, micro_area) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/exodontias/${cod_ibge}/equipe/${nu_ine}/microarea/${micro_area}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna o nome da cor de acordo com o valor
 * @param {int} valor - Valor numérico
 */
export function getCorTextoResultado(valor) {
  if (valor >= 60) {
    return 'green-color'
  } else {
    return 'red-color'
  }
}

/**
 * Converte um campo JSON (string ou array) em lista.
 *
 * @param {String|Array|null} valor
 * @returns {Array}
 */
export function parseLista(valor) {
  try {
    if (Array.isArray(valor)) return valor
    if (valor == null || valor === '') return []
    return typeof valor === 'string' ? JSON.parse(valor) : valor || []
  } catch (e) {
    return []
  }
}

/**
 * Indica se o procedimento pertence ao período selecionado.
 *
 * @param {Object} proc
 * @param {Object|null} mes
 * @returns {Boolean}
 */
export function procedimentoNoPeriodo(proc, mes) {
  if (!mes) return true

  const mesesSelecionados = mes.value || []
  const anoSelecionado = Number(mes.nu_ano)

  if (proc.nu_mes != null && proc.nu_ano != null) {
    return (
      mesesSelecionados.includes(Number(proc.nu_mes)) &&
      Number(proc.nu_ano) === anoSelecionado
    )
  }

  const dataRef = proc.dt_proced || proc.dt_atendimento
  if (!dataRef) return false

  const partes = String(dataRef).slice(0, 10).split('-')
  if (partes.length < 2) return false

  return (
    mesesSelecionados.includes(Number(partes[1])) &&
    Number(partes[0]) === anoSelecionado
  )
}

/**
 * Lista usada para contar a quantidade na coluna.
 *
 * Exodontias usa `dados`. Outros procedimentos usa `procedimentos`,
 * com fallback para `dados`.
 *
 * @param {Object} item
 * @param {Boolean} exibirOutrosProcedimentos
 * @returns {Array}
 */
export function getFonteQuantidade(item, exibirOutrosProcedimentos = false) {
  if (exibirOutrosProcedimentos) {
    const procedimentos = parseLista(item.procedimentos)
    if (procedimentos.length) return procedimentos
  }

  return parseLista(item.dados)
}

/**
 * Retorna a quantidade de procedimentos conforme o filtro.
 *
 * - Exodontias: soma procedimentos com nm diferente de 0.
 * - Outros procedimentos: soma procedimentos com nm igual a 0.
 * Se houver mês selecionado, soma apenas os procedimentos do período.
 *
 * @param {Object} item Cadastro.
 * @param {Object|null} mes Mês/período selecionado.
 * @param {Boolean} exibirOutrosProcedimentos Indica se deve contar outros procedimentos.
 * @returns {Number} Quantidade de procedimentos.
 */
export function getQuantidadeExodontias(
  item,
  mes = null,
  exibirOutrosProcedimentos = false
) {
  const lista = getFonteQuantidade(item, exibirOutrosProcedimentos)

  return lista
    .filter((proc) => {
      const isOutroProcedimento = Number(proc.nm) === 0

      if (
        exibirOutrosProcedimentos ? !isOutroProcedimento : isOutroProcedimento
      ) {
        return false
      }

      return procedimentoNoPeriodo(proc, mes)
    })
    .reduce((total, proc) => {
      return total + Number(proc.qt_proced || 0)
    }, 0)
}

/**
 * Gera o PDF da listagem de exodontias.
 *
 * @param {Array<Object>} cadastros Lista de cadastros exibidos na tela.
 * @param {Object} selectEquipe Equipe selecionada.
 * @param {String} micro_area Microárea selecionada.
 * @param {Object|String|Number|null} mes Mês selecionado no filtro.
 * @param {Boolean} exibirOutrosProcedimentos Indica se o filtro de outros procedimentos está ativo.
 */
export function generatePdf(
  cadastros,
  selectEquipe,
  micro_area,
  mes = null,
  exibirOutrosProcedimentos = false
) {
  let title_pdf = 'Exodontias'
  let titlePdfSave = utils.methods.titlePdf('Exodontias')

  let description_pdf = 'Exodontias em relação ao total de procedimentos'
  const colunaQuantidade = exibirOutrosProcedimentos
    ? 'OUTROS PROCEDIMENTOS'
    : 'EXODONTIAS'
  let headers_pdf = ['NOME', 'NASCIMENTO', 'MÃE', colunaQuantidade]
  var data_pdf = []
  for (let i = 0; i < cadastros.length; i++) {
    var obj = {}
    obj.no_cidadao = cadastros[i].no_cidadao
    obj.dt_nascimento =
      utils.methods.formatDate(cadastros[i].dt_nascimento) +
      `\n(${cadastros[i].idade} ${cadastros[i].idade > 1 ? 'anos' : 'ano'})`
    obj.no_mae = cadastros[i].no_mae
    obj.exodontias = getQuantidadeExodontias(
      cadastros[i],
      mes,
      exibirOutrosProcedimentos
    )
    data_pdf.push(obj)
  }

  let doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: 'a4',
    ...utils.methods.encryptionPdf(),
  })
  // cria a tabela
  let table = utils.methods.createTablePdf(data_pdf)

  // let brasao = store.state.infoMunicipio.brasao
  let brasao = ''

  utils.methods
    .fileToBase64(brasao)
    .then((base64data) => {
      // Header
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf,
        200,
        'l'
      )

      const dadosCabecalho = []

      const nomeMes = utils.methods.getDescricaoMes(mes)

      if (nomeMes) {
        dadosCabecalho.push({
          label: 'Período',
          value: nomeMes,
        })
      }

      dadosCabecalho.push(
        {
          label: 'Equipe',
          value: selectEquipe.no_equipe,
        },
        {
          label: 'CNES',
          value: selectEquipe.nu_cnes,
        },
        {
          label: 'INE',
          value: selectEquipe.nu_ine,
        },
        {
          label: 'Microárea',
          value: micro_area,
        }
      )

      verticalPosition = utils.methods.bodyPdf(
        doc,
        dadosCabecalho,
        verticalPosition,
        'l'
      )

      doc.autoTable({
        head: [headers_pdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 12,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 12,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: function (data) {
          let num_taxa_exod = data.row.raw[3]

          // Header sempre preto
          if (data.row.section === 'head') {
            doc.setTextColor(0, 0, 0)
            return
          }

          if (num_taxa_exod == 0) {
            doc.setTextColor(2, 99, 57)
            return
          }
          doc.setTextColor(255, 165, 0)
        },
      })

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY, 250, 600, 'l')

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

function getMesAnoAtual() {
  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())

  return `${mesAtual}/${anoAtual}`
}

function montarResultado(dados, tipoResultado = 'quadrimestral') {
  if (tipoResultado === 'mensal') {
    return {
      numerador: dados.numerador,
      denominador: dados.denominador,
      resultado: dados.resultado,
      classificacao: dados.classificacao,
    }
  }

  return {
    numerador: dados.numerador_quad,
    denominador: dados.denominador_quad,
    resultado: dados.resultado_quad,
    classificacao: dados.classificacao_quad,
  }
}

export function percentualMunicipio(
  dadosDaTabelaResultado,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDoMunicipio = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 1 &&
      item.ibge === store.state.cod_ibge &&
      item.indicador == 39 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDoMunicipio) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDoMunicipio.dados)

  return montarResultado(dados, tipoResultado)
}

export function percentualEquipe(
  dadosDaTabelaResultado,
  nuIne,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDaEquipe = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 2 &&
      item.ine === nuIne &&
      item.indicador == 39 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDaEquipe) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDaEquipe.dados)

  return montarResultado(dados, tipoResultado)
}

export function percentualMicroArea(
  dadosDaTabelaResultado,
  nu_ine,
  micro_area,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDaMicroArea = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 3 &&
      item.ine == nu_ine &&
      item.micro_area === micro_area &&
      item.indicador == 39 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDaMicroArea) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDaMicroArea.dados)

  return montarResultado(dados, tipoResultado)
}
